import {
  LANGUAGES,
  MENTION_SOURCE_TYPES,
  SEARCH_TYPES_LIST,
  SOURCE,
  SOURCES,
  SearchType,
} from "@/constants";
import { useOrganization } from "@/hooks/useOrganization";
import { useLingui } from "@lingui/react/macro";
import z from "zod";
import { QueryIn } from "./requestUtils";

export type QueryBuilderForm = {
  title: string;
  type: SearchType;
  industry: string | null;
  startDate: Date;
  endDate?: Date;
  queries: QueryIn[];
  informationNeed?: string | null;
};

export function useQueryBuilderFormSchema() {
  const { t } = useLingui();
  const { data: orgData } = useOrganization();
  const maxNumKeywordsPerQuery = orgData?.ok
    ? (orgData.data?.plan?.max_num_keywords_per_query ?? 0)
    : 0;

  let keywords = z
    .array(z.string())
    .min(1, { message: t`Vänligen ange minst 1 sökord` });

  if (maxNumKeywordsPerQuery > 0) {
    keywords = keywords.max(maxNumKeywordsPerQuery, {
      message: t`Trial-konton är begränsade till ${maxNumKeywordsPerQuery} söktermer per söksats, kontakta oss för att utöka.`,
    });
  }

  const querySchema = z.object({
    uuid: z.string().nullable().optional(),
    keywords: keywords.optional(),
    languages: z.array(z.enum(LANGUAGES)).optional(),
    sources: z.array(z.enum(SOURCES)).optional(),
    tags: z.array(z.string()).optional(),
    nearKeywords: z.array(z.string()).optional(),
    nearDistance: z
      .number()
      .positive({
        message: t`Avståndet måste vara positivt`,
      })
      .optional(),
    nearNearKeywords: z.array(z.string()).optional(),
    notNearKeywords: z.array(z.string()).optional(),
    notNearDistance: z
      .number()
      .positive({
        message: t`Avståndet måste vara positivt`,
      })
      .optional(),
    includeChannelNames: z.array(z.string().nullable().optional()).optional(),
    excludeChannelNames: z.array(z.string().nullable().optional()).optional(),
    minReachByChannelType: z
      .union([
        z.object({
          [SOURCE.YOUTUBE]: z.number().optional(),
          [SOURCE.INSTAGRAM]: z.number().optional(),
          [SOURCE.TIKTOK]: z.number().optional(),
          [SOURCE.NEWS]: z.number().optional(),
          [SOURCE.TWITTER]: z.number().optional(),
          [SOURCE.FACEBOOK]: z.number().optional(),
          has_custom_values: z.boolean(),
        }),
        z.object({ no_values: z.literal(true) }),
      ])
      .optional(),
    mentionTypes: z.enum(MENTION_SOURCE_TYPES).optional(),
    includePaywalled: z.boolean().optional(),
  });

  const queryBuilderFormSchema = z.object({
    title: z
      .string()
      .min(1, { message: t`Vänligen lägg till en titel för din bevakning` }),
    type: z.enum(SEARCH_TYPES_LIST),
    industry: z.string().nullable(),
    startDate: z.date(),
    endDate: z.date().optional(),
    queries: z.array(querySchema),
    informationNeed: z.string().nullish(),
  });

  return queryBuilderFormSchema;
}
